/**
 * Import attestation history: who confirmed they had read which policy, and
 * when, from the system a hospital is leaving.
 *
 * It follows the library import's manners: the spreadsheet is checked before
 * anything is written and the page says so while it checks; every finding is
 * counted and the two kinds are explained; a file with findings goes on only
 * by choice; the one consequential option is explained with the reason to
 * pick each answer; and a report says what came in and what did not.
 *
 * What is different is the subject. These are records ANOTHER system kept.
 * The page never calls them acknowledgements collected here, keeps them for
 * people who have left and for policies that are not in the library, and
 * lets them count toward today's requirement only when the person, the
 * policy and the version in force all match, and only if asked.
 */
import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BackLink } from '../../shared/BackLink';
import { call } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { CODE_MAX } from '../../types/fieldLimits';
import { COPIED_MS, FINDINGS_SHOWN, FindingsTable, IMPORT_MAX, LONG, StepList, UploadWindowClosed, errorText, putFile, saveFile, saveText, tooLarge, type LineFinding } from './ImportPage';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';

type Status = 'draft' | 'checked' | 'running' | 'completed' | 'failed' | 'rolled_back';
interface Summary {
  importable: number; people: number; peopleWhoAreMembers: number; policies: number; policiesInLibrary: number;
  linesForFormerStaff: number; linesForUnknownPolicies: number; eligibleToCount: number; earliest: string | null; latest: string | null; sourceSystems: string[];
}
export interface AttestationCheck {
  ok: boolean; fileProblems: string[]; lineErrors: LineFinding[]; lineErrorCount: number; warnings: LineFinding[]; warningCount: number;
  rowCount: number; headers: string[]; recognized: Record<string, string>; ignoredColumns: string[]; summary: Summary;
}
interface Stats { imported: number; leftOut: number; formerStaff: number; unlinkedPolicies: number; eligible: number; countedTowardCurrent: number; alreadyAcknowledgedHere: number; notRequiredHere: number }
export interface AttestationJob {
  id: string; orgId: string; status: Status; fileName: string | null; createdAtIso: string | null; completedAtIso: string | null; createdByEmail: string | null;
  summary: Summary | null; findings: { fileProblems: number; lineErrors: number; warnings: number; lines: number } | null;
  options: { acceptedFindings: boolean; countTowardCurrent: boolean } | null; stats: Stats | null; error: string | null; hasReport: boolean;
}

const n = (v: number) => v.toLocaleString('en-US');
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '');
const STEP_LABELS = ['Spreadsheet', 'What will happen', 'Report'];
export function stepOfAttestation(status: Status | undefined, checkedHere: boolean): number {
  if (!status || status === 'draft') return 0;
  if (status === 'checked') return checkedHere ? 1 : 0; // a reload loses the findings: check again, it is quick
  return 2;
}

export function AttestationImportPage({ orgId: orgIdProp, home = 'console' }: { orgId?: string; home?: 'console' | 'app' } = {}) {
  const routeParams = useParams<{ orgId: string }>();
  const orgId = orgIdProp ?? routeParams.orgId;
  const [params, setParams] = useSearchParams();
  const jobId = params.get('job');
  const queryClient = useQueryClient();
  const inApp = home === 'app';
  const libraryPath = inApp ? '/admin/import' : `/console/orgs/${orgId}/import`;
  const startPath = `${libraryPath}/attestations`;
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /*
   * THE SIGNED UPLOAD URL FROM CREATING THIS IMPORT.
   *
   * It was a ref read during render and never cleared, so it outlived the
   * import it belonged to: create job A, then open job B from the list, and
   * B's upload step was handed A's signed URL — which points at A's storage
   * path. B's spreadsheet would have been written into A's folder.
   *
   * Stored WITH its job and read only when that job is open, so there is no
   * reset and no window in which the id has not caught up with the value.
   */
  const [createdFor, setCreatedFor] = useState<{ jobId: string; uploadUrl: string } | null>(null);
  const uploadUrl = createdFor && createdFor.jobId === jobId ? createdFor.uploadUrl : null;

  const jobs = useQuery({
    queryKey: ['attestation-imports', orgId], enabled: Boolean(orgId) && !jobId, retry: false, staleTime: 0, refetchOnMount: 'always',
    queryFn: () => call<{ orgId: string }, { jobs: AttestationJob[] }>('listAttestationImports', { orgId: orgId! }),
  });
  const status = useQuery({
    queryKey: ['attestation-import', orgId, jobId], enabled: Boolean(orgId && jobId), retry: false, staleTime: 0,
    queryFn: () => call<{ orgId: string; jobId: string }, { job: AttestationJob }>('getAttestationImport', { orgId: orgId!, jobId: jobId! }),
  });
  const create = useMutation({
    mutationFn: () => call<{ orgId: string }, { jobId: string; uploadUrl: string }>('createAttestationImport', { orgId: orgId! }),
    onSuccess: (r) => {
      setCreatedFor({ jobId: r.jobId, uploadUrl: r.uploadUrl });
      setParams({ job: r.jobId });
    },
    onError: (e) => setFailure(errorText(e, 'The import could not be started.')),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['attestation-import', orgId, jobId] });
  if (!orgId) return null;
  const job = status.data?.job;

  const subtitle = <>Who confirmed they had read which policy, and when, from the system you are leaving. Kept as a record from that system, including for staff who have since left. Nothing is written until you press Import, and an import can be rolled back.</>;
  const arrow = jobId
    ? <BackLink to={startPath} label="Back to the start of attestation history" direct />
    : <BackLink to={libraryPath} label="Back to Import library" direct />;

  return (
    <>
      {inApp ? (
        <header className={page.header}>
          <div className={page.titled}>
            <div className={page.titleLine}>{arrow}<h1 className={page.title}>Import attestation history</h1></div>
            <p className={page.subtitle}>{subtitle}</p>
          </div>
        </header>
      ) : (
        <div className={page.titled}>
          <div className={page.titleLine}>{arrow}<h1 className={own.title}>Import attestation history</h1></div>
          <p className={own.subtitle}>{subtitle}</p>
        </div>
      )}
      <div className={inApp ? page.body : undefined}>
        {failure && <div className={own.error} role="alert">{failure}</div>}
        {notice && <div className={`${page.notice} ${page.noticeInfo}`} role="status">{notice}</div>}

        {!jobId && <StartView jobs={jobs.data?.jobs} loading={jobs.isLoading} loadError={jobs.error ? errorText(jobs.error, 'The list of previous attestation imports could not be loaded. Reload the page to try again.') : null} starting={create.isPending} onStart={() => { setFailure(null); setNotice(null); create.mutate(); }} onOpen={(id) => { setNotice(null); setParams({ job: id }); }} />}

        {jobId && status.isLoading && <Spinner label="Loading the import" />}
        {jobId && status.error && (
          <PageState title="Could not load this import" tone="alert" action={{ to: startPath, label: 'Back to all attestation imports' }}>
            {errorText(status.error, 'The import did not come back. Reload the page to try again.')}
          </PageState>
        )}
        {jobId && job && (
          <JobView key={job.id} job={job} orgId={orgId} firstUploadUrl={uploadUrl} onChanged={() => void refresh()} onError={setFailure}
            onGone={(message) => { setParams({}); void queryClient.invalidateQueries({ queryKey: ['attestation-imports', orgId] }); setNotice(message); }} />
        )}
      </div>
    </>
  );
}

function StartView({ jobs, loading, loadError, starting, onStart, onOpen }: { jobs: AttestationJob[] | undefined; loading: boolean; loadError: string | null; starting: boolean; onStart: () => void; onOpen: (id: string) => void }) {
  const template = useQuery({ queryKey: ['attestation-template'], retry: false, queryFn: () => call<Record<string, never>, { template: string; headers: string[] }>('getAttestationTemplate', {}) });
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return (
    <>
      <section className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
        <h2 className={page.sectionTitle}>What the spreadsheet needs</h2>
        <p className={page.itemMeta} style={{ maxWidth: '84ch', margin: '0 0 var(--sp-3)' }}>
          One attestation per line, as a CSV. Every line needs three things: <strong>which policy</strong> (its number, or its title), <strong>who</strong> (an email address, an employee number, or a name) and <strong>when</strong>.
          A <strong>version</strong> column is what lets an attestation count for the version in force today; without it the line is history only.
          Most systems can export this as an acknowledgment or signature report. Import the library first, so each line can be tied to its policy.
        </p>
        {template.data && (
          <>
            <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)' }}>Columns we understand: <code>{template.data.headers.join(', ')}</code>. Common names from other systems are recognized too.</p>
            <div className={page.actions} style={{ marginBottom: 'var(--sp-4)' }}>
              <button type="button" className={page.btn} style={{ minWidth: 96 }} onClick={() => { void navigator.clipboard?.writeText(template.data!.template).then(() => { setCopied(true); if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => setCopied(false), COPIED_MS); }).catch(() => setCopied(false)); }}>{copied ? 'Copied' : 'Copy CSV'}</button>
              <button type="button" className={page.btn} onClick={() => saveText('attestation-history-example.csv', template.data!.template)}>Download CSV example</button>
              <span className="vh" role="status">{copied ? 'The example CSV was copied.' : ''}</span>
            </div>
          </>
        )}
        <button type="button" className={`${own.googleBtn} ${own.primaryWhenReady}`} style={{ width: 'auto', padding: '9px 16px' }} disabled={starting} onClick={onStart}>{starting ? 'Starting…' : 'Start an attestation import'}</button>
      </section>
      <section className={own.panel} style={{ padding: 'var(--sp-5)' }}>
        <h2 className={page.sectionTitle}>Previous attestation imports</h2>
        {loading ? <Spinner label="Loading imports" /> : loadError ? (
          // Not "None yet": a failed read is not an empty history.
          <div className={`${page.notice} ${page.noticeDanger}`} role="alert">{loadError}</div>
        ) : (jobs?.length ?? 0) === 0 ? (
          <p className={page.itemMeta}>No attestation imports yet. Each one is listed here once started, and can be rolled back from here.</p>
        ) : (
          <ul className={page.list} aria-label="Previous attestation imports">
            {jobs!.map((j) => (
              <li key={j.id} className={page.listItem}>
                <div>
                  <div className={page.itemName}>{j.fileName || 'No spreadsheet yet'} <span className={`${own.pill} ${j.status === 'completed' ? own.pillActive : j.status === 'failed' ? own.pillOther : own.pillDraft}`} style={{ marginLeft: 8 }}>{j.status.replace('_', ' ')}</span></div>
                  <div className={page.itemMeta}>
                    {j.createdAtIso && <>{new Date(j.createdAtIso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} · </>}
                    {j.status === 'completed' && j.stats ? `${n(j.stats.imported)} imported${j.stats.leftOut ? `, ${n(j.stats.leftOut)} left out` : ''}` : j.findings ? `${n(j.findings.lines)} lines read, nothing imported` : 'nothing imported'}
                  </div>
                </div>
                <button type="button" className={page.btn} aria-label={`Open ${j.fileName || 'this import'}, ${j.id}`} onClick={() => onOpen(j.id)}>Open</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function JobView({ job, orgId, firstUploadUrl, onChanged, onError, onGone }: { job: AttestationJob; orgId: string; firstUploadUrl: string | null; onChanged: () => void; onError: (m: string) => void; onGone: (message: string) => void }) {
  const [result, setResult] = useState<{ fileName: string; check: AttestationCheck; findingsCsv: string | null } | null>(null);
  // The check moves the job on; the findings live on this page, so the step follows them, not a stale status.
  const readable = Boolean(result && result.check.fileProblems.length === 0);
  const current = job.status === 'draft' || job.status === 'checked' ? (readable ? 1 : 0) : stepOfAttestation(job.status, readable);
  return (
    <>
      <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>Import <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{job.id}</span> · {job.status.replace('_', ' ')}</p>
      <StepList steps={STEP_LABELS} current={current} />
      {(job.status === 'draft' || job.status === 'checked') && (
        <CheckAndChoose job={job} orgId={orgId} firstUploadUrl={firstUploadUrl} result={result} setResult={setResult} onRan={onChanged} onChecked={onChanged} onError={onError} />
      )}
      {job.status === 'running' && <section className={own.panel} style={{ padding: 'var(--sp-5)' }}><Spinner label="Importing the attestations" /></section>}
      {(job.status === 'completed' || job.status === 'failed' || job.status === 'rolled_back') && <Done job={job} orgId={orgId} onChanged={onChanged} onError={onError} />}
      {job.status !== 'running' && <DeleteRecord job={job} orgId={orgId} onError={onError} onDeleted={(kept) => onGone(kept > 0 ? `The record of that import was deleted. The ${n(kept)} ${kept === 1 ? 'attestation it imported is' : 'attestations it imported are'} still on record.` : 'The record of that import was deleted.')} />}
    </>
  );
}

function CheckAndChoose({ job, orgId, firstUploadUrl, result, setResult, onRan, onChecked, onError }: {
  job: AttestationJob; orgId: string; firstUploadUrl: string | null; result: { fileName: string; check: AttestationCheck; findingsCsv: string | null } | null;
  setResult: (r: { fileName: string; check: AttestationCheck; findingsCsv: string | null } | null) => void; onRan: () => void; onChecked: () => void; onError: (m: string) => void;
}) {
  const [csv, setCsv] = useState<File | null>(null);
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'checking'>('idle');
  const [accept, setAccept] = useState(false);
  const [counting, setCounting] = useState<'history' | 'count'>('history');
  const url = useRef(firstUploadUrl);
  const csvProblem = tooLarge('csv', csv);

  async function put(file: File) {
    const fresh = async () => (await call<{ orgId: string; jobId: string }, { uploadUrl: string }>('getAttestationUploadUrl', { orgId, jobId: job.id })).uploadUrl;
    if (!url.current) url.current = await fresh();
    try { await putFile(url.current, file, 'text/csv'); } catch (e) {
      if (!(e instanceof UploadWindowClosed)) throw e;
      url.current = await fresh(); await putFile(url.current, file, 'text/csv');
    }
  }
  const check = useMutation({
    mutationFn: async () => {
      if (!csv) throw new Error('Choose the spreadsheet first.');
      setResult(null); setAccept(false); setCounting('history');
      setPhase('uploading'); await put(csv);
      setPhase('checking');
      const r = await call<{ orgId: string; jobId: string; csvFileName: string }, { check: AttestationCheck; findingsCsv: string | null }>('checkAttestationImport', { orgId, jobId: job.id, csvFileName: csv.name }, LONG);
      return { fileName: csv.name, ...r };
    },
    onSuccess: (r) => { setResult(r); onChecked(); },
    onError: (e) => onError(errorText(e, 'The spreadsheet could not be checked.')),
    onSettled: () => setPhase('idle'),
  });
  const run = useMutation({
    mutationFn: () => call<Record<string, unknown>, unknown>('runAttestationImport', { orgId, jobId: job.id, ...(accept ? { acceptFindings: true } : {}), ...(counting === 'count' ? { countTowardCurrent: true } : {}) }, LONG),
    onSuccess: onRan,
    onError: (e) => onError(errorText(e, 'The import could not run.')),
  });

  const c = result?.check ?? null;
  const unreadable = Boolean(c && c.fileProblems.length > 0);
  const errors = c?.lineErrorCount ?? 0, warnings = c?.warningCount ?? 0, findings = errors + warnings;
  const s = c?.summary ?? null;
  const busy = phase !== 'idle';
  const canRun = Boolean(c && !unreadable && s && s.importable > 0 && (findings === 0 || accept));

  return (
    <>
      <section className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
        <h2 className={page.sectionTitle}>1. The spreadsheet</h2>
        <div className={form.field} style={{ marginBottom: 'var(--sp-3)' }}>
          <label className={form.label} htmlFor="att-csv">Attestation history (CSV)</label>
          <p className={form.hint} id="att-csv-limits" style={{ margin: '0 0 var(--sp-2)' }}>Maximum {IMPORT_MAX.csvMb} MB and {n(IMPORT_MAX.rows)} lines. A longer history goes in as several imports, by year for example.</p>
          <input id="att-csv" type="file" accept=".csv,text/csv" disabled={busy || run.isPending} aria-describedby="att-csv-limits" aria-invalid={csvProblem ? true : undefined} onChange={(e: ChangeEvent<HTMLInputElement>) => { setCsv(e.target.files?.[0] ?? null); setResult(null); }} />
          {csvProblem && <p className={form.error} role="alert">{csvProblem}</p>}
        </div>
        <button type="button" className={`${own.googleBtn} ${own.primaryWhenReady}`} style={{ width: 'auto', padding: '9px 16px' }} disabled={!csv || Boolean(csvProblem) || busy || run.isPending} onClick={() => check.mutate()}>
          {busy ? 'Checking…' : result ? 'Check it again' : 'Check the spreadsheet'}
        </button>
        {!result && job.status === 'checked' && !busy && <p className={form.hint}>This import was checked before ({job.fileName}). Choose the spreadsheet and check it again to see what it found and go on.</p>}

        <div role="status" aria-live="polite" style={{ marginTop: 'var(--sp-3)' }}>
          {busy && csv && (
            <div className={`${page.notice} ${page.noticeInfo}`}>
              <strong>Checking {csv.name} before anything is imported.</strong>
              <ol style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.3em', lineHeight: 1.7 }}>
                <li>{phase === 'uploading' ? 'Uploading the spreadsheet…' : 'Spreadsheet uploaded.'}</li>
                <li style={{ color: phase === 'checking' ? undefined : 'var(--muted)' }}>{phase === 'checking' ? 'Reading every line, and matching each person and policy against this organization…' : 'Then every line is read and matched against this organization.'}</li>
              </ol>
              Nothing is imported at this step.
            </div>
          )}
        </div>

        {c && unreadable && (
          <div role="alert" className={`${page.notice} ${page.noticeDanger}`}>
            <strong>{result!.fileName} cannot be imported.</strong> The file itself is the problem, so there is nothing to import from it:
            <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.2em' }}>{c.fileProblems.map((p) => <li key={p}>{p}</li>)}</ul>
            Correct the file, choose it again, and check it again.
          </div>
        )}
      </section>

      {c && !unreadable && s && (
        <section className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
          <h2 className={page.sectionTitle}>2. What will happen</h2>
          <div className={`${page.notice} ${page.noticeInfo}`} role="status">
            <strong>{n(s.importable)} of the {n(c.rowCount)} {c.rowCount === 1 ? 'line' : 'lines'} can be imported</strong>: attestations by {n(s.people)} {s.people === 1 ? 'person' : 'people'} to {n(s.policies)} {s.policies === 1 ? 'policy' : 'policies'}
            {s.earliest && <>, dated {day(s.earliest)}{s.latest && s.latest.slice(0, 10) !== s.earliest.slice(0, 10) ? <> to {day(s.latest)}</> : null}</>}
            {s.sourceSystems.length > 0 && <>, from {s.sourceSystems.join(', ')}</>}.
            <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.2em', lineHeight: 1.6 }}>
              <li>{n(s.peopleWhoAreMembers)} of those people {s.peopleWhoAreMembers === 1 ? 'is a member' : 'are members'} here today. <strong>{n(s.linesForFormerStaff)} {s.linesForFormerStaff === 1 ? 'attestation is' : 'attestations are'} by people who are not</strong>, usually staff who have left. They are kept under the person's name, address and employee number, attached to no account, because a surveyor may still ask for them.</li>
              <li>{n(s.policiesInLibrary)} of those policies {s.policiesInLibrary === 1 ? 'is' : 'are'} in the library here. <strong>{n(s.linesForUnknownPolicies)} {s.linesForUnknownPolicies === 1 ? 'attestation is' : 'attestations are'} to a policy that is not</strong>. They are kept under the name the old system gave and linked to nothing. Importing the library first, with matching policy numbers, links them.</li>
            </ul>
          </div>

          {findings > 0 && (
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <div role="alert" className={`${page.notice} ${errors > 0 ? page.noticeDanger : page.noticeWarn}`}>
                <strong>{n(findings)} {findings === 1 ? 'finding' : 'findings'} in {n(c.rowCount)} {c.rowCount === 1 ? 'line' : 'lines'}: {n(errors)} {errors === 1 ? 'error' : 'errors'} and {n(warnings)} {warnings === 1 ? 'warning' : 'warnings'}.</strong>
                <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.2em', lineHeight: 1.6 }}>
                  <li><strong>An error</strong> means the line cannot be kept: nobody is named, no policy is named, or the date cannot be read. If you go on, that line is <strong>left out</strong>.</li>
                  <li><strong>A warning</strong> means the line <strong>is imported</strong>, with something you should know: the person is not a member today, the policy is not in the library, or it is for an older version. A repeated line is the one exception: it is left out as a repeat.</li>
                </ul>
              </div>
              {c.lineErrors.length > 0 && <FindingsTable caption={`Errors: ${n(errors)} ${errors === 1 ? 'line' : 'lines'} that would be left out`} findings={c.lineErrors} total={errors} />}
              {c.warnings.length > 0 && <FindingsTable caption={`Warnings: ${n(warnings)} ${warnings === 1 ? 'line' : 'lines'} with something to know`} findings={c.warnings} total={warnings} />}
              {result?.findingsCsv && <p style={{ marginTop: 'var(--sp-3)' }}><button type="button" className={page.btn} onClick={() => saveText(`${result.fileName.replace(/\.csv$/i, '')}-findings.csv`, result.findingsCsv!)}>Download the full list ({n(findings)}) as a CSV</button></p>}
              <fieldset style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: 'var(--sp-4)', margin: 'var(--sp-4) 0 0' }}>
                <legend className={form.label} style={{ padding: '0 var(--sp-2)' }}>What would you like to do about the findings?</legend>
                <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)' }}><strong>Correct the spreadsheet</strong> when the errors are worth fixing, choose it again and check it again. Warnings about former staff and older versions are normal for a history and need no correcting. Or go on:</p>
                <label style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start', lineHeight: 1.5 }}>
                  <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} style={{ marginTop: 3 }} />
                  <span><strong>Import what can be imported.</strong> Keep the {n(s.importable)} {s.importable === 1 ? 'line' : 'lines'} that can be kept{c.rowCount - s.importable > 0 ? <> and leave out the other {n(c.rowCount - s.importable)}</> : null}. The report made when the import finishes lists every line that was imported, every line that was not, and why.</span>
                </label>
              </fieldset>
            </div>
          )}

          <fieldset disabled={run.isPending} style={{ border: 0, padding: 0, margin: 'var(--sp-5) 0 0' }}>
            <legend className={page.sectionTitle} style={{ fontSize: 15, width: '100%' }}>Should these count toward what staff owe today?</legend>
            {s.eligibleToCount === 0 ? (
              <p className={page.itemMeta} style={{ maxWidth: '84ch' }}>
                <strong>None of these can.</strong> An attestation can stand in for today's requirement only when the person is a member here, the policy is in the library, and the version attested is the version in force.
                {Object.prototype.hasOwnProperty.call(c.recognized, 'version') ? ' No line meets all three.' : ' This spreadsheet has no version column, so none can be tied to the version in force.'} They are imported as history.
              </p>
            ) : (
              <>
                <p className={page.itemMeta} style={{ maxWidth: '84ch', margin: '0 0 var(--sp-2)' }}>
                  <strong>{n(s.eligibleToCount)} of the {n(s.importable)}</strong> {s.eligibleToCount === 1 ? 'is' : 'are'} by a member here, to a policy in the library, for the version in force today. Only {s.eligibleToCount === 1 ? 'that one' : 'those'} can count; the rest are history whichever you choose.
                </p>
                <label className={own.choice} data-on={counting === 'history' || undefined}>
                  <input type="radio" name="att-count" checked={counting === 'history'} onChange={() => setCounting('history')} />
                  <span>
                    <strong>Keep them as history only.</strong> Recommended unless you are sure the text is unchanged.
                    <span className={own.choiceWhy}>Nothing changes for staff: anyone asked to acknowledge a policy here is still asked. The imported attestations appear on each policy's acknowledgement report under “Before StandingDocs”. Pick this when policies were edited on the way in, or when you would rather everyone confirm once in the new system.</span>
                  </span>
                </label>
                <label className={own.choice} data-on={counting === 'count' || undefined}>
                  <input type="radio" name="att-count" checked={counting === 'count'} onChange={() => setCounting('count')} />
                  <span>
                    <strong>Also count them toward today's requirement.</strong>
                    <span className={own.choiceWhy}>Where the person behind one of those {n(s.eligibleToCount)} still owes that policy here, it is marked acknowledged as of the date they attested in the old system, and labeled as counted from an imported attestation. They are not asked again for that version. Pick this when the policy text is the same as it was in the old system, so re-asking would be asking twice. It can be undone: rolling this import back reopens exactly those.</span>
                  </span>
                </label>
              </>
            )}
          </fieldset>

          <div style={{ marginTop: 'var(--sp-4)' }}>
            <button type="button" className={`${own.googleBtn} ${own.primaryWhenReady}`} style={{ width: 'auto', padding: '9px 16px' }} disabled={!canRun || run.isPending} onClick={() => run.mutate()}>
              {run.isPending ? 'Importing…' : `Import ${n(s.importable)} ${s.importable === 1 ? 'attestation' : 'attestations'}`}
            </button>
            {findings > 0 && !accept && <span className={page.itemMeta} style={{ marginLeft: 'var(--sp-3)' }}>Choose what to do about the findings first.</span>}
          </div>
        </section>
      )}
    </>
  );
}

function Done({ job, orgId, onChanged, onError }: { job: AttestationJob; orgId: string; onChanged: () => void; onError: (m: string) => void }) {
  const [arming, setArming] = useState(false);
  const st = job.stats;
  const report = useMutation({
    mutationFn: () => call<{ orgId: string; jobId: string }, { xlsx: { fileName: string; base64: string } }>('exportAttestationImportReport', { orgId, jobId: job.id }, LONG),
    onSuccess: (r) => saveFile(r.xlsx, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'),
    onError: (e) => onError(errorText(e, 'The report could not be downloaded.')),
  });
  const rollback = useMutation({
    mutationFn: () => call<{ orgId: string; jobId: string }, { removed: number; reopened: number }>('rollbackAttestationImport', { orgId, jobId: job.id }, LONG),
    onSuccess: () => { setArming(false); onChanged(); },
    onError: (e) => onError(errorText(e, 'The rollback failed.')),
  });
  return (
    <section className={own.panel} style={{ padding: 'var(--sp-5)' }}>
      <h2 className={page.sectionTitle}>{job.status === 'completed' ? 'Done' : job.status === 'rolled_back' ? 'Rolled back' : 'Stopped part-way'}</h2>
      {job.error && <div className={own.error} role="alert">{job.error}</div>}
      {job.status === 'rolled_back' && <p className={page.itemMeta}>Everything this import wrote has been taken back: its attestations are gone and any requirement it marked acknowledged is open again.</p>}
      {st && job.status !== 'rolled_back' && (
        <>
          <dl className={page.facts}>
            <div><dt className={page.factTerm}>Imported</dt><dd className={`${page.factValue} tnum`}>{n(st.imported)}</dd></div>
            <div><dt className={page.factTerm}>Left out</dt><dd className={`${page.factValue} tnum`}>{n(st.leftOut)}</dd></div>
            <div><dt className={page.factTerm}>By former staff</dt><dd className={`${page.factValue} tnum`}>{n(st.formerStaff)}</dd></div>
            <div><dt className={page.factTerm}>Policy not in library</dt><dd className={`${page.factValue} tnum`}>{n(st.unlinkedPolicies)}</dd></div>
            <div><dt className={page.factTerm}>Counted toward today</dt><dd className={`${page.factValue} tnum`}>{n(st.countedTowardCurrent)}</dd></div>
          </dl>
          <p className={page.itemMeta} style={{ maxWidth: '84ch', marginTop: 'var(--sp-3)' }}>
            {job.options?.countTowardCurrent
              ? <>You chose to count them. Of the {n(st.eligible)} that could, <strong>{n(st.countedTowardCurrent)} closed a requirement that was still open</strong>, {n(st.alreadyAcknowledgedHere)} were already acknowledged here, and {n(st.notRequiredHere)} were not required of that person here.</>
              : <>They were imported as <strong>history only</strong>; nothing staff owe today changed.</>}
            {' '}They appear on each policy's acknowledgement report under “Before StandingDocs”, and in its export, marked as imported.
          </p>
        </>
      )}
      {job.hasReport && (
        <div style={{ marginTop: 'var(--sp-5)' }}>
          <h3 className={page.sectionTitle} style={{ fontSize: 15 }}>After-action report</h3>
          <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)', maxWidth: '78ch' }}>Created when the import finished and kept with it: what was imported, what was not and why, and what came in with a note.</p>
          <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={report.isPending} onClick={() => report.mutate()}>{report.isPending ? 'Preparing…' : 'Download the report (Excel)'}</button>
        </div>
      )}
      {(job.status === 'completed' || job.status === 'failed') && (
        <div style={{ marginTop: 'var(--sp-4)', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          {!arming ? <button type="button" className={page.btn} onClick={() => setArming(true)}>Roll this import back</button> : (
            <>
              <span className={page.itemMeta}>Removes every attestation this import brought in{(st?.countedTowardCurrent ?? 0) > 0 ? `, and reopens the ${n(st!.countedTowardCurrent)} it closed` : ''}. Sure?</span>
              <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={rollback.isPending} onClick={() => rollback.mutate()}>{rollback.isPending ? 'Rolling back…' : 'Yes, roll it back'}</button>
              <button type="button" className={page.btn} onClick={() => setArming(false)}>Keep it</button>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function DeleteRecord({ job, orgId, onDeleted, onError }: { job: AttestationJob; orgId: string; onDeleted: (kept: number) => void; onError: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  const id = useId();
  const remove = useMutation({
    mutationFn: () => call<{ orgId: string; jobId: string; confirm: string }, { attestationsKept: number }>('deleteAttestationImportRecord', { orgId, jobId: job.id, confirm: word.trim().toLowerCase() }),
    onSuccess: (r) => onDeleted(r.attestationsKept ?? 0),
    onError: (e) => onError(errorText(e, 'The record could not be deleted.')),
  });
  const imported = job.status === 'completed' ? job.stats?.imported ?? 0 : 0;
  const typed = word.trim().toLowerCase() === 'delete';
  return (
    <section className={`${own.panel} ${own.dangerPanel}`} aria-labelledby={`${id}-t`} style={{ padding: 'var(--sp-5)', marginTop: 'var(--sp-5)' }}>
      <h2 className={page.sectionTitle} id={`${id}-t`}>Delete the record of this import</h2>
      <p className={page.itemMeta} style={{ maxWidth: '80ch', margin: '0 0 var(--sp-3)' }}>
        This removes the record only: its entry under Previous attestation imports, the spreadsheet you uploaded, and its report.
        {imported > 0 ? <> <strong>The {n(imported)} {imported === 1 ? 'attestation it imported stays' : 'attestations it imported stay'} on record.</strong> After this the import can no longer be rolled back. Download the report first if you want to keep it.</> : <> <strong>It imported nothing, so no attestation is affected.</strong></>}
      </p>
      {!open ? <button type="button" className={`${page.btn} ${own.dangerBtn}`} onClick={() => { setWord(''); setOpen(true); }}>Delete this record…</button> : (
        <form onSubmit={(e) => { e.preventDefault(); if (typed) remove.mutate(); }} style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label className={form.label} htmlFor={`${id}-w`}>Type <strong>delete</strong> to confirm</label>
            <input id={`${id}-w`} type="text" className={form.input} style={{ width: 200 }} maxLength={CODE_MAX} value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false} disabled={remove.isPending} autoFocus />
          </div>
          <button type="submit" className={page.btn} style={typed ? { borderColor: 'var(--danger-ink)', color: 'var(--danger-ink)' } : undefined} disabled={!typed || remove.isPending}>{remove.isPending ? 'Deleting…' : 'Delete the record'}</button>
          <button type="button" className={page.btn} disabled={remove.isPending} onClick={() => setOpen(false)}>Keep it</button>
        </form>
      )}
    </section>
  );
}

export { FINDINGS_SHOWN };
